/**
 * Habit recurrence engine — pure functions answering "is this habit due on a
 * given day?" and "what are the next due days?".
 *
 * See .claude/tasks/habits-feature.md (D2, D9, D10a, D11) for the model.
 * All dates are local calendar days. The anchor for interval frequencies and
 * the earliest due day is `habit.startDate` ("ab wann").
 */
import type { Habit, HabitFrequency, Weekday } from '../db/models'
import {
  addDays,
  clampDayOfMonth,
  dateKey,
  daysBetween,
  monthsBetween,
  startOfDay,
  weekday,
  weeksBetween,
} from '../date/calendar'

/**
 * Is the habit "scheduled" (actionable) on `date`?
 *
 * For fixed-day frequencies this is the real due-day test. For the flexible
 * `timesPerWeek` quota there is no fixed due day — it is actionable every day
 * (≥ startDate) and quota satisfaction is decided separately via
 * {@link isQuotaMet}. Returns false for any date before `startDate`.
 */
export function isDueOn(frequency: HabitFrequency, startDate: Date, date: Date): boolean {
  // Before the habit started → never due / never counted as missed.
  if (daysBetween(startOfDay(startDate), startOfDay(date)) < 0) return false

  switch (frequency.kind) {
    case 'daily':
    case 'timeOfDay':
      return true

    case 'timesPerWeek':
      // No fixed day — actionable every day; quota handled elsewhere.
      return true

    case 'weekdays':
      return frequency.days.includes(weekday(date))

    case 'monthly':
      return date.getDate() === clampDayOfMonth(date, frequency.day)

    case 'interval': {
      const { every, unit, weekdays } = frequency
      if (every < 1) return false
      if (unit === 'day') {
        return daysBetween(startOfDay(startDate), startOfDay(date)) % every === 0
      }
      if (unit === 'week') {
        if (weeksBetween(startDate, date) % every !== 0) return false
        return weekdays && weekdays.length > 0
          ? weekdays.includes(weekday(date))
          : weekday(date) === weekday(startDate)
      }
      // unit === 'month'
      if (monthsBetween(startDate, date) % every !== 0) return false
      return date.getDate() === clampDayOfMonth(date, startDate.getDate())
    }

    default:
      return false
  }
}

/** Convenience wrapper that also respects pause/soft-delete on the habit. */
export function isHabitDueOn(habit: Pick<Habit, 'frequency' | 'startDate' | 'pausedAt' | 'deletedAt'>, date: Date): boolean {
  if (habit.deletedAt) return false
  if (habit.pausedAt) return false
  return isDueOn(habit.frequency, habit.startDate, date)
}

/**
 * The next `count` due-day keys ('YYYY-MM-DD') strictly after `from`
 * (exclusive). Used for the read-only future preview in the date strip.
 * `timesPerWeek` returns every upcoming day (it is actionable daily).
 */
export function nextDueDates(
  frequency: HabitFrequency,
  startDate: Date,
  from: Date,
  count: number,
  maxLookaheadDays = 366,
): string[] {
  const out: string[] = []
  let cursor = addDays(from, 1)
  for (let i = 0; i < maxLookaheadDays && out.length < count; i++) {
    if (isDueOn(frequency, startDate, cursor)) out.push(dateKey(cursor))
    cursor = addDays(cursor, 1)
  }
  return out
}

/**
 * Number of completed logs inside the Mon–So week containing `anyDayInWeek`.
 * `completedDateKeys` is the set of 'YYYY-MM-DD' keys with completed === true.
 */
export function weekCompletionCount(completedDateKeys: Set<string>, anyDayInWeek: Date): number {
  const monday = addDays(anyDayInWeek, -weekday(anyDayInWeek))
  let n = 0
  for (let i = 0; i < 7; i++) {
    if (completedDateKeys.has(dateKey(addDays(monday, i)))) n++
  }
  return n
}

/** Is the `timesPerWeek` quota met in the week containing `anyDayInWeek`? */
export function isQuotaMet(count: number, completedDateKeys: Set<string>, anyDayInWeek: Date): boolean {
  return weekCompletionCount(completedDateKeys, anyDayInWeek) >= count
}

export type { Weekday }
